import { setCookie } from 'util/cookie-util';
import formatProductVariant from 'util/format-product-variant';

import { lazy, useContext, useEffect, useState } from 'react';

import { OTEventName } from 'const/oentok-event';
import { useBasket } from 'context/basket-context';
import { useCall } from 'context/call-context';
import { useStoreDetails } from 'context/store-context';
import getProductByHandle from 'data/get-product-by-handle';
import useInventory from 'hooks/useInventory';
import { useProductExpert } from 'hooks/useProductExpert';
import { CallWindowType, type Product, type Variant } from 'types';

import { ChatContext } from '../chat-window/chat-window';
import TextChat from '../text-chat/text-chat';
import VideoClient from '../video-client/video-client';

import classes from './product-expert.module.scss';

const ProductBanner = lazy(() => import('./product-banner/product-banner'));
const ProductGallery = lazy(() => import('./product-gallery/product-gallery'));
const ProductHeader = lazy(() => import('./product-header/product-header'));
const ProductIntro = lazy(() => import('./product-intro/product-intro'));
const ProductOverview = lazy(() => import('./product-overview/product-overview'));
const ProductVideos = lazy(() => import('./product-videos/product-videos'));
const WebpageFrame = lazy(() => import('./webpage-frame/WebpageFrame'));

export type ProductExpertPropType = {
  session: OT.Session;
  agentName: string;
  expertName: string;
  mode: string;
  variant?: Variant;
};

export interface ExpertState {
  openView: string;
  minimized: boolean;
  mode: string;
  chatEnabled: boolean;
  message: string;
  messages: IMessages[];
  newMessageCount: number;
}

export interface IMessages {
  sessionId: string;
  message: string;
}

const ProductExpert = ({ session, agentName, mode, expertName, variant }: ProductExpertPropType): JSX.Element => {
  const {
    callSettings,
    id,
    name: storeName,
    productConf,
    currency,
    widgetOption,
    currencyConversionRate
  } = useStoreDetails();
  const isWindowType = widgetOption.callWindowType === CallWindowType.WINDOW;

  const initialCallState: ExpertState = {
    chatEnabled: false,
    message: '',
    messages: [],
    minimized: false,
    mode: mode,
    newMessageCount: 0,
    openView: 'INTRO'
  };
  const [callState, setCallState] = useState<ExpertState>(initialCallState);
  const { product: currentProduct, setProduct: setCurrentProduct } = useCall();
  const [price, setPrice] = useState<number | undefined>();
  const [selectedVariant, setSelectedVariant] = useState<Variant | undefined>(variant);
  const [halfVideo, setHalfVideo] = useState<boolean>(false);
  const [webShareLink, setWebShareLink] = useState<string | undefined>();

  const { onClickCheckout } = useProductExpert();
  const { closeProductExpert } = useContext(ChatContext)!;
  const { removeAll, cartItems } = useBasket();
  const { setSelectedVariantId, availableStock, isLoading } = useInventory(currentProduct);

  const handleSignalShareView = (data: { view: string; message: string }) => {
    if (data.view === 'SPECIFIC') {
      setCallState((prevState) => ({
        ...prevState,
        minimized: true,
        openView: 'SPECIFIC'
      }));
    } else if (data.view === 'VIDEO') {
      setCallState((prevState) => ({
        ...prevState,
        minimized: true,
        openView: 'VIDEO'
      }));
    } else if (data.view === 'GALLERY') {
      setCallState((prevState) => ({
        ...prevState,
        minimized: true,
        openView: 'GALLERY'
      }));
    } else if (data.view === 'INTRO') {
      setCallState((prevState) => ({
        ...prevState,
        minimized: true,
        openView: 'INTRO'
      }));
    } else if (data.view === 'WEBPAGE') {
      setWebShareLink(data?.message);
      setCallState((prevState) => ({
        ...prevState,
        minimized: true,
        openView: 'WEBPAGE'
      }));
    }
  };

  const handleSignalShareEvent = () => {
    session.on(OTEventName.SHARE, async (event) => {
      // @ts-ignore
      const data = JSON.parse(event.data);
      setCallState((prevState) => ({ ...prevState, chatEnabled: false }));

      if (data?.productHandle) {
        const productDetails: Product | undefined = await getProductByHandle({
          currencyConversionRate,
          productName: data.productHandle,
          storeId: id
        });

        if (productDetails) {
          productDetails.variants = formatProductVariant(productDetails.variants, currency);

          if (data.variantId) {
            setSelectedVariant(productDetails?.variants?.find((v) => v.id === data.variantId));
          } else {
            setSelectedVariant(undefined);
          }

          setCurrentProduct(productDetails);
        }
      }
      handleSignalShareView(data);
    });
  };

  const handleSignalTextEvent = () => {
    session.on(OTEventName.TEXT, async (event) => {
      // @ts-ignore
      const data: { sessionId: string; message: string } = JSON.parse(event.data);

      setCallState((prevState) => ({
        ...prevState,
        chatEnabled: true,
        messages: [
          ...prevState.messages,
          {
            message: data.message,
            sessionId: data.sessionId
          }
        ],
        newMessageCount:
          data.sessionId !== session.connection?.connectionId
            ? prevState.newMessageCount + 1
            : prevState.newMessageCount
      }));
    });
  };

  function handleConnectionDestroy() {
    session.on('connectionDestroyed', async () => {
      closeProductExpertView();
    });
  }

  const handleSignalVariantEvent = () => {
    session.on(OTEventName.SHARE_VARIANT, async (event) => {
      // @ts-ignore
      const VariantShareData = JSON.parse(event.data);
      setSelectedVariant(VariantShareData);
    });
  };

  useEffect(() => {
    // clear state for redirect the cart after merge to cart
    sessionStorage.removeItem('checkOuting');

    const expiryDate = new Date();
    localStorage.setItem('SessionId', session?.sessionId);
    expiryDate.setDate(expiryDate.getDate() + 31);
    localStorage.setItem('ConferWithSessionExpiry', expiryDate.toISOString());
    setCookie('SessionId', session?.sessionId, 31);
    handleSignalShareEvent();
    handleSignalTextEvent();
    handleConnectionDestroy();
    handleSignalVariantEvent();
  }, []);

  useEffect(() => {
    setHalfVideo(callState.minimized);
    setPrice(currentProduct?.price);
  }, [currentProduct, callState.minimized]);

  const closeProductExpertView = () => {
    if (cartItems && cartItems.length > 0 && cartItems.find((item) => !item.mergedToCart)) {
      void onClickCheckout();
    } else {
      close();
    }
  };

  const close = () => {
    sessionStorage.setItem('checkOuting', (cartItems.length > 0).toString());
    // @ts-ignore
    session.signal({
      data: JSON.stringify({
        message: 'CUSTOMER HAS LEFT THE VIDEO SESSION',
        type: 'LEFT'
      }),
      type: 'NOTIFICATION'
    });
    // clear cart when closing expert view
    removeAll(session);
    closeProductExpert();
  };

  const onClickEndCall = () => {
    void onClickCheckout(close);
  };

  const onClickResizeButton = () => {
    setHalfVideo(!halfVideo);
  };

  return (
    <>
      <ProductHeader isWindowType={isWindowType} />
      <div className={classes.cwProductExpert}>
        <div
          className={classes.cwProductVideoSide}
          style={halfVideo || callState.mode === 'CHAT' ? { minHeight: isWindowType ? '45vh' : '50vh' } : {}}
        >
          <VideoClient
            onClickResizeButton={onClickResizeButton}
            session={session}
            agentName={agentName}
            expertName={expertName}
            closeProductExpertView={onClickEndCall}
            callState={callState}
            setCallState={setCallState}
            settings={callSettings}
            storeName={storeName}
            halfVideo={halfVideo}
            isWindowType={isWindowType}
          />
          {(callState.chatEnabled || callState.mode === 'CHAT') && (
            <TextChat
              session={session}
              chatState={callState}
              setChatState={setCallState}
            />
          )}
        </div>
        <div className={classes.cwProductSide}>
          {!currentProduct && <ProductBanner imgUrl={productConf?.defaultProductImage} />}

          {currentProduct && callState.openView === 'INTRO' && (
            <ProductIntro
              currentProduct={currentProduct}
              agentName={agentName}
              storeName={storeName}
              priceOnChange={setPrice}
              price={price ?? 0}
              variant={selectedVariant}
              currencySymbol={currency.symbol}
              setSelectedVariantId={setSelectedVariantId}
              availableStock={availableStock}
              isLoading={isLoading}
            />
          )}

          {currentProduct && callState.openView === 'GALLERY' && currentProduct?.galleryImages && (
            <ProductGallery
              currentProduct={currentProduct}
              session={session}
              price={price ?? 0}
              agentName={agentName}
              storeName={storeName}
            />
          )}

          {currentProduct && callState.openView === 'SPECIFIC' && (
            <ProductOverview
              currentProduct={currentProduct}
              price={price ?? 0}
              agentName={agentName}
              storeName={storeName}
            />
          )}

          {currentProduct && callState.openView === 'VIDEO' && (
            <ProductVideos
              currentProduct={currentProduct}
              session={session}
              price={price ?? 0}
              agentName={agentName}
              storeName={storeName}
            />
          )}
          {currentProduct && callState.openView === 'WEBPAGE' && webShareLink && (
            <WebpageFrame webShareLink={webShareLink} />
          )}
        </div>
      </div>
    </>
  );
};

export default ProductExpert;
